import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Box, Divider} from 'views/components';
import {HoverToolBox, translate} from 'views/module';

import Message from './Message';
import EmailMessage from './EmailMessage';

import displayResource from 'utils/miscellaneous/display_resource';
import parseSesEmail from 'utils/miscellaneous/email/parse_ses_email';
import moment from 'moment';

const MessageFeedBox = (props) => {
  const emptyEmail = {
    from: '',
    to: '',
    replyTo: '',
    messageId: '',
    inReplyTo: '',
    references: '',
    subject: '',
    date: '',
    html: '',
    text: '',
  };
  const [toolbarVisible, setToolbarVisible] = React.useState(false);
  const [mouseOver, setMouseOver] = React.useState(false);
  const [editMode, setEditMode] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [parsedEmail, setParsedEmail] = React.useState(emptyEmail);

  const {
    message,
    userRn,
    clientBackend,
    relationships,
    onReplyToMessage,
  } = props;

  const isMessageTypeEmail = message.messageType === 'EMAIL';
  const emailBlob = message?.body?.blob;
  const showEmailMessage = isMessageTypeEmail && message?.body?.editorType !== 'MARKDOWN';

  React.useEffect(() => {
    let isActive = true;

    if (!showEmailMessage) {
      setParsedEmail(emptyEmail);
      return () => {
        isActive = false;
      };
    }

    setParsedEmail(emptyEmail);

    parseSesEmail(emailBlob).then((value) => {
      if (!isActive) {
        return;
      }

      setParsedEmail(value);
    });

    return () => {
      isActive = false;
    };
  }, [emailBlob, showEmailMessage]);

  React.useEffect(() => {
    if (editMode) {
      setToolbarVisible(false);
      return;
    }
    setToolbarVisible(mouseOver);
  }, [editMode, mouseOver]);

  const submittedBy = message.submittedBy;
  const author = displayResource(submittedBy);
  const replyRelationship = React.useMemo(() => {
    const candidateAddresses = [
      parsedEmail.replyTo,
      parsedEmail.from,
      parsedEmail.to,
    ].map((value) => {
      const matchedEmail = value.match(/<([^>]+)>/);

      if (matchedEmail?.[1]) {
        return matchedEmail[1].trim().toLowerCase();
      }

      return value.trim().toLowerCase();
    }).filter(Boolean);

    if (candidateAddresses.length === 0) {
      return null;
    }

    const foundMatchingRelationshipByEmail = Object.values(relationships).find((relationship) => {
      const relationshipEmail = relationship.contacts?.find((contact) => contact.type === 'Email')?.detail;

      return relationshipEmail && candidateAddresses.includes(relationshipEmail.trim().toLowerCase());
    }) || null;

    return foundMatchingRelationshipByEmail;
  }, [parsedEmail.from, parsedEmail.replyTo, parsedEmail.to, relationships]);

  const timestampInfoCalculator = () => {
    const messageType = message.messageType;
    const createdAt = message.createdAt;

    let timestampPrefixLabel = null;
    switch (messageType) {
      case 'ANNOUNCEMENTS':
        timestampPrefixLabel = translate('client.ClientDetail.Correspondance.Correspondance.AnnouncedOn');
        break;
      case 'COMMENTS':
        timestampPrefixLabel = translate('client.ClientDetail.Correspondance.Correspondance.CommentedOn');
        break;
      case 'CALENDLY_UPDATE':
        timestampPrefixLabel = translate('client.ClientDetail.Correspondance.Correspondance.CalendlyUpdate');
        break;
      case 'DIALPAD_SMS_TEXT':
        timestampPrefixLabel = translate('client.ClientDetail.Correspondance.Correspondance.DialpadSms');
        break;
      case 'DIALPAD_CALL_TRANSCRIPT':
        timestampPrefixLabel = translate('client.ClientDetail.Correspondance.Correspondance.DialpadCallTranscripts');
        break;
      case 'EMAIL':
        timestampPrefixLabel = translate('client.ClientDetail.Correspondance.Correspondance.Email');
        break;
      default:
        timestampPrefixLabel = translate('client.ClientDetail.Correspondance.Correspondance.CalendlyUpdate');
        break;
    }

    const formattedTimestamp = moment(createdAt).format(translate('timeFormat.VerboseDay'));

    return `${timestampPrefixLabel} ${formattedTimestamp}`;
  };


  const onClickEdit = () => {
    setEditMode(true);
  };

  const onClickEditSave = (event, newMessage, setLoading) => {
    clientBackend.messages.update(message.messageRn, newMessage,
        () => {
          setEditMode(false);
        }, () => {
          setEditMode(false);
        });
  };

  const onClickEditCancel = () => {
    setEditMode(false);
  };

  const onEmojiSelected = (emoji) => {
    const prevMessage = message;
    const prevEmoji = prevMessage.emoji;
    // find if we previously selected this emoji, then remove, else add
    if (prevEmoji) {
      const prevEmojiUsers = prevEmoji[emoji];
      if (prevEmojiUsers) {
        const requesterIndex = prevEmojiUsers.findIndex((user) => user === userRn);
        if (requesterIndex > -1) {
          clientBackend.messages.removeEmoji(message.messageRn, emoji, userRn);
          return;
        }
      }
    }
    clientBackend.messages.addEmoji(message.messageRn, emoji, userRn);
  };

  const onClickDelete = (setIsDeleting) => {
    setIsDeleting(true);
    clientBackend.messages.delete(
        message.messageRn,
        () => setIsDeleting(false),
        () => setIsDeleting(false),
    );
  };

  const onClickReply = () => {
    if (!replyRelationship) {
      return;
    }

    const replySubjectPrefix = translate('client.ClientDetail.Correspondance.Correspondance.ReplyBuilder.SubjectPrefix');
    const replyDateFormat = translate('client.ClientDetail.Correspondance.Correspondance.ReplyBuilder.DateFormat');
    let replySubject = parsedEmail.subject || '';
    if (!replySubject.toLowerCase().startsWith(replySubjectPrefix.toLowerCase())) {
      replySubject = replySubject.trim() ? `${replySubjectPrefix} ${replySubject}` : replySubjectPrefix;
    }

    const replyDate = moment(parsedEmail.date);
    const formattedDate = replyDate.isValid() ?
      replyDate.format(replyDateFormat) :
      parsedEmail.date;
    const replyAuthor = parsedEmail.from || parsedEmail.replyTo || parsedEmail.to;
    const replyDateAndAuthor = [formattedDate, replyAuthor].filter(Boolean).join(', ');
    const replyHeader = replyDateAndAuthor ?
      translate('client.ClientDetail.Correspondance.Correspondance.ReplyBuilder.Header', {
        replyPrefix: replyDateAndAuthor,
      }) :
      translate('client.ClientDetail.Correspondance.Correspondance.ReplyBuilder.HeaderFallback');
    const referenceMatches = parsedEmail.references.match(/<[^>]+>/g) || [];
    const references = Array.from(new Set([
      ...referenceMatches,
      parsedEmail.messageId,
    ].filter(Boolean)));

    onReplyToMessage({
      relationshipRn: replyRelationship.relationshipRn,
      subject: replySubject,
      inReplyTo: parsedEmail.messageId || '',
      references,
      replyHeader,
      quotedHtml: parsedEmail.html || '',
      quotedText: parsedEmail.text || '',
    });
  };

  return (
    <Box>
      <Divider/>
      <Box
        onMouseOver={(e) => setMouseOver(true) & setAnchorEl(e.currentTarget)}
        onMouseLeave={(e) => setMouseOver(false)}
        sx={{
          backgroundColor: toolbarVisible ? 'background.highlighted': 'inherit',
          px: {md: 2, xs: 1},
        }}
      >
        {
          showEmailMessage &&
          <EmailMessage
            parsedEmail={parsedEmail}
            timestampLabel={timestampInfoCalculator()}
          />
        }
        {
          !showEmailMessage &&
          <Message
            userRn={userRn}
            fullName={author}
            message={message.body.blob}
            emojis={message.emoji}
            editMode={editMode}
            onClickSave={onClickEditSave}
            onClickCancel={onClickEditCancel}
            onEmojiSelected={onEmojiSelected}
            emojisWithRequesterData={message.emojisWithRequesterData}
            timestampLabel={timestampInfoCalculator()}
            truncate
          />
        }
        <HoverToolBox
          toolBar={toolbarVisible}
          anchorEl={anchorEl}
          copiedText={message.body.blob}
          onClickEdit={onClickEdit}
          onClickDelete={onClickDelete}
          onClickReply={onClickReply}
          onEmojiSelected={onEmojiSelected}
          hideTool={{
            emoji: isMessageTypeEmail,
            reply: !showEmailMessage || !replyRelationship,
            copy: showEmailMessage,
            edit: isMessageTypeEmail || userRn !== message.submittedBy.resourceRn,
            delete: userRn !== message.submittedBy.resourceRn,
          }}
        />
      </Box>
    </Box>
  );
};
MessageFeedBox.propTypes = {
  message: PropTypes.object.isRequired,
  clientBackend: PropTypes.object.isRequired,
  userRn: PropTypes.string.isRequired,
  relationships: PropTypes.object.isRequired,
  onReplyToMessage: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  userRn: state.user.userRn,
});

export default connect(mapStateToProps, dispatch)(React.memo(MessageFeedBox));
